import {
  InputMatrix,
  MatrixConfigurationResponse,
} from "../../../../../../api/models/test-series/TestSeriesMatrixConfiguration";
import matrixConfigurationOptionsMap from "./MatrixConfigurationOptionsMap";
import matrixConfigurationOptionsReverseMap from "./MatrixConfigurationOptionsReverseMap";
import {
  HotCoordinate,
  gatherParameterEntries,
  gatherParameterKeys,
  gatherParameterValues,
} from "./MatrixHotTableDataUtil";
import { MatrixParameterValue } from "../components/MatrixInput/interfaces/MatrixInputCell";

type MatrixHotTableCell = MatrixParameterValue | string | undefined;

type MatrixHotTableData = MatrixHotTableCell[][];

// eslint-disable-next-line no-shadow
enum ConfigurationParameterName {
  SUBSTRATE = "Substrate",
  SUBSTRATE_2 = "Substrate 2",
  PRODUCT = "Product",
  PROPERTIES = "Properties",
  BUILDING_ACTIONS = "Building Actions",
  SPECIMEN_TREATMENT = "Specimen Treatment",
  CONDITIONS = "Conditions",
  REPETITION = "Repetition",
}

class MatrixHotTableMapper {
  inputMatrix: InputMatrix;

  matrixConfiguration: MatrixConfigurationResponse;

  disabledCells: HotCoordinate[];

  constructor(inputMatrix: InputMatrix, matrixConfiguration: MatrixConfigurationResponse) {
    this.inputMatrix = inputMatrix;
    this.matrixConfiguration = matrixConfiguration;
    this.disabledCells = [];
  }

  createHotData = (): MatrixHotTableData => {
    const headerMatrixSection = this.renderHeaderSection();
    const valueMatrixSection = this.renderValueSection();
    return [...headerMatrixSection, ...valueMatrixSection];
  };

  private renderHeaderSection = (): MatrixHotTableData => {
    const { rows, columns } = this.matrixConfiguration;
    const outputMatrix: MatrixHotTableData = [...rows].map((row) => [
      ...columns.map(() => ""),
      matrixConfigurationOptionsMap.get(row),
    ]);

    const { inputMatrixColumnParameterGroups = [] } = this.inputMatrix;
    // eslint-disable-next-line no-shadow
    inputMatrixColumnParameterGroups.forEach(({ inputMatrixRowParameterGroups }, i) => {
      inputMatrixRowParameterGroups.forEach(({ rowParameterValues }) => {
        const entries = gatherParameterEntries(rowParameterValues);

        if (i === 0) {
          const findRowIndexByParameter = (parameter: string) => {
            const rowIndex = outputMatrix.findIndex((row) => row.includes(parameter));
            return rowIndex;
          };

          const findRowIndexByParameterInEntries = (parameter: string) => {
            const rowIndex = entries.findIndex((row) => row.includes(parameter));
            return rowIndex;
          };

          Object.values(ConfigurationParameterName).forEach((name) => {
            const key = matrixConfigurationOptionsReverseMap.get(name)!;
            const index = findRowIndexByParameterInEntries(key);
            if (index >= 0 && entries[index][0] === key) {
              const rowIndex = findRowIndexByParameter(name);
              outputMatrix[rowIndex].push(entries[index][1].value);
            }
          });
        }
      });
    });

    const firstColumnGroup = inputMatrixColumnParameterGroups[0];
    const columnParameterHeaders = gatherParameterKeys(firstColumnGroup?.columnParameterValues ?? {});
    columnParameterHeaders.forEach((header, i) => {
      outputMatrix[rows.length - 1][i] = matrixConfigurationOptionsMap.get(header)!;
    });

    return outputMatrix;
  };

  private renderValueSection = (): MatrixHotTableData => {
    const { inputMatrixColumnParameterGroups = [] } = this.inputMatrix;

    const outputMatrix: MatrixHotTableData = [...Array(inputMatrixColumnParameterGroups.length)].map(
      () => [] as MatrixHotTableCell[],
    );

    // eslint-disable-next-line no-shadow
    inputMatrixColumnParameterGroups.forEach(({ columnParameterValues, inputMatrixRowParameterGroups }, rowIndex) => {
      const values = gatherParameterValues(columnParameterValues ?? {});
      values.forEach((value) => {
        outputMatrix[rowIndex].push(value);
      });
      outputMatrix[rowIndex].push("");

      let columnIndex = outputMatrix[rowIndex].length;
      (inputMatrixRowParameterGroups ?? []).forEach(({ repetition }) => {
        outputMatrix[rowIndex][columnIndex] = repetition as MatrixParameterValue;
        // eslint-disable-next-line no-plusplus
        columnIndex++;
      });
    });
    return outputMatrix;
  };
}

export default MatrixHotTableMapper;
export type { MatrixHotTableData };
